/**
 * A frota da família, em tabela.
 *
 * Papel: mostra uma linha por veículo, com o código, a marca, o modelo, o ano,
 * a placa, o chassi, o renavam, a blindagem, a observação e, no fim, a
 * categoria e a situação no seguro.
 *
 * Como usar: quem monta a tabela passa `onEditar` e `onRemover` apenas para
 * quem tem nível `editar`; sem eles a coluna de ações some inteira. Categoria e
 * Seguro deverão continuar como colunas, e não só no formulário: quem tem `ver`
 * não abre o formulário, e sem as duas colunas não leria esses dados em lugar
 * nenhum da tela.
 *
 * A coluna Seguro reúne numa célula o que o banco guarda em duas —
 * `foraDaApolice` e `classeBonus` —, porque a ausência de seguro e a classe de
 * bônus não anotada não são a mesma coisa, e `situacaoNoSeguro` escreve cada
 * uma de um jeito. A lotação e o código FIPE ficam só no formulário.
 */
import { type Veiculo } from '@concierge/shared';

import { formatarPlaca } from '@/shared/lib/formato';
import { Button } from '@/shared/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

interface TabelaVeiculosProps {
  veiculos: Veiculo[];
  /** Chamado com o veículo da linha; ausente esconde a coluna de ações. */
  onEditar?: (veiculo: Veiculo) => void;
  /** Chamado com o veículo a excluir; a confirmação é de quem monta. */
  onRemover?: (veiculo: Veiculo) => void;
}

/**
 * Escreve a situação do veículo no seguro.
 *
 * São três respostas e não duas: o veículo fora da apólice, o que está nela com
 * a classe anotada, e o que está nela sem ninguém ter anotado a classe — este
 * último devolve travessão, e é por isso que as duas colunas existem separadas
 * no banco.
 *
 * @param veiculo - O veículo da linha.
 * @returns "fora da apólice", a classe por extenso ("classe 4"), ou o
 *   travessão de quem está na apólice sem classe anotada.
 */
function situacaoNoSeguro(veiculo: Veiculo): string {
  if (veiculo.foraDaApolice) return 'fora da apólice';
  return veiculo.classeBonus === null ? '—' : `classe ${String(veiculo.classeBonus)}`;
}

/**
 * A tabela da frota.
 *
 * @param veiculos - Os veículos a mostrar, já em ordem.
 * @param onEditar - Chamado ao clicar em editar; ausente esconde as ações.
 * @param onRemover - Chamado ao clicar em remover; ausente esconde as ações.
 * @returns A tabela com uma linha por veículo.
 */
export function TabelaVeiculos({ veiculos, onEditar, onRemover }: TabelaVeiculosProps) {
  const mostrarAcoes = onEditar !== undefined || onRemover !== undefined;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>ID</TableHead>
          <TableHead>Marca</TableHead>
          <TableHead>Modelo</TableHead>
          <TableHead>Ano</TableHead>
          <TableHead>Placa</TableHead>
          <TableHead>Chassi</TableHead>
          <TableHead>Renavam</TableHead>
          <TableHead>Blindado</TableHead>
          <TableHead>Observações</TableHead>
          <TableHead>Categoria</TableHead>
          <TableHead>Seguro</TableHead>
          {mostrarAcoes && (
            <TableHead>
              <span className="sr-only">Ações</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {veiculos.map((veiculo) => (
          <TableRow key={veiculo.id}>
            <TableCell className="tabular">{veiculo.codigo}</TableCell>
            <TableCell>{veiculo.marca}</TableCell>
            <TableCell className="text-texto-medio">{veiculo.modelo}</TableCell>
            <TableCell className="tabular">{veiculo.ano ?? '—'}</TableCell>
            <TableCell className="tabular">{formatarPlaca(veiculo.placa)}</TableCell>
            <TableCell className="tabular">{veiculo.chassi ?? '—'}</TableCell>
            <TableCell className="tabular">{veiculo.renavam ?? '—'}</TableCell>
            <TableCell>{veiculo.blindado ? 'Sim' : 'Não'}</TableCell>
            <TableCell className="text-[12.5px] text-texto-fraco">
              {veiculo.observacao ?? '—'}
            </TableCell>
            <TableCell>{veiculo.categoria}</TableCell>
            <TableCell>{situacaoNoSeguro(veiculo)}</TableCell>
            {mostrarAcoes && (
              <TableCell>
                <div className="flex gap-2">
                  {onEditar !== undefined && (
                    <Button variant="outline" size="sm" onClick={() => onEditar(veiculo)}>
                      editar
                    </Button>
                  )}
                  {onRemover !== undefined && (
                    <Button variant="destructive" onClick={() => onRemover(veiculo)}>
                      remover
                    </Button>
                  )}
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
